import { useEffect, useCallback } from 'react';
import { gql } from '@apollo/client';
import { useApolloClient, useLazyQuery } from '@apollo/client/react';
import { useNavigate } from 'react-router';
import { TypeOf, z } from 'zod';
import GET_ACCOUNTS_Q from '../gql/index.gql';

import { Card } from '@repo/ui/Card';

import { registerInput, useZodForm } from '@repo/hooks/useZodForm';

import { Form, TextInputField, TextAreaField } from '@repo/ui/Form';
import { Button } from '@repo/ui/Button';

import { toQueryList } from '@repo/util/toQueryList';

import debounce from 'lodash/debounce';
import { coerceValue } from '@repo/util/coerceValueForQuery';

export interface CreateSubAccountFormProps {
  accountId: string;
}

const CreateSubAccountForm = ({ accountId }: CreateSubAccountFormProps) => {
  const navigate = useNavigate();

  //Validation for in-use names
  const [getAccounts, { data }] = useLazyQuery(GET_ACCOUNTS_Q);
  const currentName = data?.accounts?.edges[0]?.node?.name;

  const createSubAccountFormSchema = z.object({
    name: z
      .string()
      .min(2, { message: 'Name must contain at least 2 characters.' })
      .refine((value) => value !== currentName, {
        message: 'This account name already exists. Please select another.',
      }),
    notes: z.string().optional(),
  });

  type CreateSubAccountFormParams = TypeOf<typeof createSubAccountFormSchema>;

  // Mutations
  const CREATE_SUB_ACCOUNT = gql`
    mutation CreateAccount($input: CreateAccountInput!) {
      createAccount(input: $input) {
        account {
          id
        }
      }
    }
  `;

  const CREATE_API_KEY_FOR_ACCOUNT = gql`
    mutation CreateApiKeyForAccount($accountId: ID!, $input: CreateApiKeyForAccountInput!) {
      createApiKeyForAccount(accountId: $accountId, input: $input) {
        apiKey {
          id
          account {
            id
          }
        }
      }
    }
  `;

  const client = useApolloClient();
  const onSubmit = async ({ ...input }: CreateSubAccountFormParams) => {
    try {
      const accountCreateRes = (await client.mutate({
        mutation: CREATE_SUB_ACCOUNT,
        variables: {
          input,
          accountId,
        },
      })) as { data?: any };

      if (accountCreateRes && accountCreateRes.data) {
        const newAccountId = accountCreateRes.data.createAccount.account.id;

        (await client.mutate({
          mutation: CREATE_API_KEY_FOR_ACCOUNT,
          variables: {
            accountId: newAccountId,
            input: {
              name: 'Account Key',
            },
          },
        })) as { data?: any };

        if (accountCreateRes && accountCreateRes.data) {
          setTimeout(() => {
            navigate(`/a/${newAccountId}/develop`);
          }, 2000);
        }
      }
    } finally {
    }
  };

  const form = useZodForm({
    schema: createSubAccountFormSchema,
    onSubmit,
  });

  const debouncedGetAccounts = useCallback(
    debounce((value) => {
      getAccounts({
        variables: {
          queryList: [
            ...toQueryList(
              {
                name: value || '',
              },
              coerceValue,
              []
            ),
          ],

          first: 1,
        },
      });
    }, 500),
    [getAccounts]
  );

  useEffect(() => {
    const subscription = form.watch(({ name }) => {
      debouncedGetAccounts(name);
    });

    return () => {
      subscription.unsubscribe();
      debouncedGetAccounts.cancel();
    };
  }, [form, debouncedGetAccounts]);

  return (
    <>
      <Card>
        <Form {...form}>
          <Form.Success>
            Sub account successfully created.
            <br /> Redirecting to new account.
          </Form.Success>
          <Form.Error>
            {(message) =>
              message.includes('already been taken')
                ? 'This account name has already been taken. Please choose another.'
                : 'Unable to create sub account. Please try again later or contact support if error persists.'
            }
          </Form.Error>
          <TextInputField label="Name" {...registerInput(form, 'name')} />
          <TextAreaField label="Description" {...registerInput(form, 'notes')} />

          <div className="w-full">
            <Button type="submit">Create Sub Account</Button>
          </div>
        </Form>
      </Card>
    </>
  );
};

export default CreateSubAccountForm;
